import React, { useEffect, useState } from "react";
import dataService from "../../services/dataService";
import ProductListEnumType from "../../../enums/productListEnumType";
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { OptionGroup, ProductListItem } from "@/app/models/productList/types";
import { LoadingStatusEnum } from "@/enums/loadingStatusEnum";

interface CartToProductListSidebarProps {
    open: boolean;
    onClose: () => void;
    onCartToNewTemplate: (name?: string, description?: string, type?: ProductListEnumType) => void;
    onCartToTemplate: (id: string, type?: ProductListEnumType) => void;
}

const MAX_NAME = 50;
const MAX_DESC = 150;

const CartToProductListSidebar: React.FC<CartToProductListSidebarProps> = ({ open, onClose, onCartToNewTemplate, onCartToTemplate }) => {
    const [type, setType] = useState<"Template" | "Estimate">("Template");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [templateGroups, setTemplateGroups] = useState<OptionGroup[]>([]);
    const [selectedValue, setSelectedValue] = useState<string>("");
    const [savedMessage, setSavedMessage] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatusEnum>(LoadingStatusEnum.IDLE);

    useEffect(() => {
        const fetchTemplateGroups = async () => {
            setLoading(true);
            try {
                const result: OptionGroup[] = await dataService.fetchEstimatesAndTemplates();
                setTemplateGroups(result);
                const first = result?.[0]?.options?.[0]?.id;
                if (first) setSelectedValue(first);
            } catch (error) {
                console.error("Failed to load templates:", error);
            } finally {
                setLoading(false);
            }
        };

        if (open) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            fetchTemplateGroups();
        }
    }, [open]);

    const handleAdd = () => {
        setLoadingStatus(LoadingStatusEnum.LOADING);
        setName("");
        const selectedItem = templateGroups
            .flatMap(group => group.options)
            .find(option => option.id == selectedValue);
        if (selectedItem) {
            setLoadingStatus(LoadingStatusEnum.SUCCESS);
            onCartToTemplate(selectedItem.id, selectedItem.listType);
        }
    };

    const handelNewListCreation = async () => {
        setLoadingStatus(LoadingStatusEnum.LOADING);

        if (!name.trim()) {
            setLoadingStatus(LoadingStatusEnum.IDLE);

            return;
        }

        onClose();

        setTimeout(() => {
            onCartToNewTemplate(name.trim(), description.trim(), type == "Template" ? ProductListEnumType.PrivateList : ProductListEnumType.QuoteList);
            setSavedMessage("");
            setLoadingStatus(LoadingStatusEnum.SUCCESS);
            setName("");
        }, 1000);
    };

    return (
        <div className={`cart-sideBar sidebarCreateNewEstimateContainer sidebar ${open ? "open" : ""}`}>
            <div className="sidebarClose" onClick={onClose}>
                <CloseIcon />
            </div>

            <div className="sidebarAddNewListBody sidebarBody">
                <h3 className="title">Create new Template or Estimate</h3>

                <div style={{ margin: "15px 0px" }}>
                    <label className="radioLabel">
                        <input
                            type="radio"
                            checked={type === "Template"}
                            onChange={() => {
                                setType("Template");
                                setLoadingStatus(LoadingStatusEnum.IDLE);
                            }} />{" "}
                        Template
                    </label>
                    <label className="radioLabel" style={{ marginLeft: "16px" }}>
                        <input
                            type="radio"
                            checked={type === "Estimate"}
                            onChange={() => {
                                setType("Estimate");
                                setLoadingStatus(LoadingStatusEnum.IDLE);
                            }} />{" "}
                        Homeowner Estimate
                    </label>
                </div>

                <div className="templateDetails" style={{ marginBottom: "20px" }}>
                    <input
                        placeholder={type === "Template" ? "Template Name" : "Estimate Name"}
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setLoadingStatus(LoadingStatusEnum.IDLE);
                        }} maxLength={MAX_NAME}
                    />
                    <label className="textSmall">Maximum of {MAX_NAME} characters</label>
                </div>

                <div className="templateDetails" style={{ marginBottom: "20px" }}>
                    <textarea
                        placeholder="Add Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        maxLength={MAX_DESC}
                        style={{ minHeight: "200px" }}
                        rows={4}
                    />
                    <label className="textSmall">Maximum of {MAX_DESC} characters</label>
                </div>

                <div className="buttons" style={{ textAlign: "right" }}>
                    <button
                        disabled={name.trim().length === 0 || LoadingStatusEnum.LOADING === loadingStatus}
                        className="handCursor blueButtonOutline"
                        onClick={handelNewListCreation}>
                        SAVE
                    </button>
                </div>

                <div style={{ marginTop: "24px" }}>
                    <h3 style={{ textAlign: "center" }}>- Or -</h3>
                    <div className="text-center" style={{ fontSize: "14px", marginBottom: "4px" }}>
                        Add to existing Template or Homeowner Estimate
                    </div>

                    <Autocomplete<ProductListItem>
                        disablePortal
                        fullWidth
                        size="small"
                        options={templateGroups.flatMap(group => group.options)}
                        groupBy={(option) => {
                            const group = templateGroups.find(g => g.options.some(o => o.id === option.id));
                            return group?.label ?? "";
                        }}
                        getOptionLabel={(option) => option.name}
                        value={
                            templateGroups.flatMap(g => g.options).find(opt => opt.id === selectedValue) || null
                        }
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        onChange={(_, newValue) => {
                            setSelectedValue(newValue?.id ?? "");
                            setLoadingStatus(LoadingStatusEnum.IDLE);
                        }}
                        componentsProps={{
                            popper: {
                                sx: {
                                    zIndex: 10000,
                                },
                            },
                        }}
                        renderGroup={(params) => (
                            <li key={params.key}>
                                <div style={{ fontWeight: 'bold', padding: "0px 15px" }}>{params.group}</div>
                                <ul style={{ paddingLeft: 16 }}>{params.children}</ul>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                fullWidth
                                className="form-input"
                                placeholder="Search list..."
                                InputLabelProps={{ shrink: false }}
                                size="small"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading && <CircularProgress size={20} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {savedMessage && (
                        <div style={{ marginLeft: "20px", marginTop: "0.5em", color: "#29b05f", fontWeight: "bold" }}>
                            {savedMessage}
                        </div>
                    )}
                    <div className="buttons" style={{ textAlign: "right" }}>
                        <button
                            disabled={selectedValue.length === 0 || loadingStatus === LoadingStatusEnum.LOADING}
                            className="handCursor blueButtonOutline"
                            onClick={handleAdd}>
                            Add
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartToProductListSidebar;